import { useState } from "react";
import { Link } from "react-router-dom";
import { Zap, Send } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await api.post("/subscribe", { email });
      toast.success("Subscribed — new papers will land in your inbox.");
      setEmail("");
    } catch (err) {
      toast.error(apiError(err, "Subscription failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer className="border-t border-[#1E2638] bg-[#0F131C]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="flex items-center gap-2.5 mb-4">
            <span className="w-8 h-8 rounded-md bg-sky-400/10 border border-sky-400/40 flex items-center justify-center">
              <Zap className="w-4 h-4 text-sky-400" />
            </span>
            <span className="font-display font-bold text-lg">SoverTech</span>
          </div>
          <p className="text-sm text-slate-400 leading-relaxed max-w-sm">
            Independent white papers on frontier technology. Free to read, funded
            directly by readers, built in public.
          </p>
        </div>
        <div className="md:col-span-3">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-sky-400 mb-4">Navigate</p>
          <div className="flex flex-col gap-2.5 text-sm text-slate-400">
            <Link to="/library" className="hover:text-sky-400 transition-colors" data-testid="footer-link-library">White Paper Library</Link>
            <Link to="/faq" className="hover:text-sky-400 transition-colors" data-testid="footer-link-faq">FAQ &amp; Answers</Link>
            <Link to="/donate" className="hover:text-sky-400 transition-colors" data-testid="footer-link-donate">Donations Hub</Link>
            <Link to="/#manifesto" className="hover:text-sky-400 transition-colors" data-testid="footer-link-manifesto">Manifesto</Link>
            <Link to="/admin" className="hover:text-sky-400 transition-colors" data-testid="footer-link-admin">Admin Console</Link>
          </div>
        </div>
        <div className="md:col-span-4">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-sky-400 mb-4">Get new papers</p>
          <form onSubmit={subscribe} className="flex gap-2" data-testid="subscribe-form">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@domain.com"
              data-testid="subscribe-email-input"
              className="flex-1 bg-[#131824] border border-[#1E2638] rounded-md px-3.5 py-2.5 text-sm text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-sky-400/60 transition-colors"
            />
            <button
              type="submit"
              disabled={busy}
              data-testid="subscribe-submit-btn"
              className="bg-sky-400 text-[#07090E] rounded-md px-4 py-2.5 font-semibold text-sm hover:bg-sky-300 transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              <Send className="w-3.5 h-3.5" /> Join
            </button>
          </form>
          <p className="mt-3 text-xs text-slate-600">No spam. Papers only. Unsubscribe anytime.</p>
        </div>
      </div>
      <div className="border-t border-[#1E2638]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-600">
            © 2026 SoverTech Research Fund — All papers open access
          </p>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-600">
            Est. 2026 · Built in public
          </p>
        </div>
      </div>
    </footer>
  );
}
